/**
 * Item 3 (Jun-2026, User-Wunsch) — „Wer ruft an?" / Rufnummer-Erkennung.
 * Manuelle Nummern-Suche + Test-Auslöser (simuliert einen eingehenden Anruf,
 * der als Live-Screen-Pop bei der Abteilung erscheint).
 */
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Phone, Search, PhoneIncoming, Loader2, Info, PhoneCall, History } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import CallerDossier from "../components/caller/CallerDossier";
import CultureHintCard from "../components/culture/CultureHintCard";

const DEPARTMENTS = [
    { code: "zentrale", label: "Zentrale" },
    { code: "verkauf", label: "Verkauf" },
    { code: "werkstatt", label: "Werkstatt" },
    { code: "buchhaltung", label: "Buchhaltung" },
];

export default function CallerLookupPage() {
    const [number, setNumber] = useState("");
    const [dept, setDept] = useState("zentrale");
    const [dossier, setDossier] = useState(null);
    const [busy, setBusy] = useState("");
    const [logs, setLogs] = useState([]);

    const loadLogs = useCallback(() => {
        api.get("/dealer/call-logs?limit=15").then((r) => setLogs(r.data?.items || [])).catch(() => {});
    }, []);
    useEffect(() => { loadLogs(); }, [loadLogs]);

    const lookup = async (num) => {
        const n = (typeof num === "string" ? num : number).trim();
        if (!n) { toast.error("Bitte Rufnummer eingeben"); return; }
        setNumber(n);
        setBusy("lookup");
        try {
            const r = await api.get(`/dealer/caller-lookup?number=${encodeURIComponent(n)}`);
            setDossier(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Suche fehlgeschlagen");
        } finally { setBusy(""); }
    };

    const testCall = async () => {
        setBusy("test");
        try {
            const r = await api.post("/dealer/caller-id/test", { number: number.trim() || "+49 170 1234567", department: dept });
            setDossier(r.data.dossier);
            loadLogs();
            toast.success("Test-Anruf ausgelöst — Screen-Pop erscheint live.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Test fehlgeschlagen");
        } finally { setBusy(""); }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-3xl mx-auto px-4 py-8">
                <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#111827] mb-4"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
                <h1 className="font-display font-black text-3xl text-[#111827] mb-1 flex items-center gap-2"><PhoneIncoming className="w-7 h-7 text-indigo-600" /> Rufnummer-Erkennung</h1>
                <p className="text-sm text-gray-500 mb-6">Erkennt den Kunden anhand seiner Rufnummer und zeigt Fahrzeuge, Vorgänge & offene Rechnungen. Im Live-Betrieb erscheint dies automatisch als Screen-Pop in der zuständigen Abteilung.</p>

                {/* Jul-2026 (User-Wunsch) — Firmen-DNA: Telefon-Leitfäden direkt im Arbeitskontext. */}
                <CultureHintCard area="telefonie" />

                <div className="bg-white border border-gray-200 rounded-sm p-4 mb-5" data-testid="caller-lookup-form">
                    <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1">Rufnummer</label>
                    <div className="flex gap-2 mb-3">
                        <div className="relative flex-1">
                            <Phone className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                            <Input value={number} onChange={(e) => setNumber(e.target.value)} onKeyDown={(e) => e.key === "Enter" && lookup()} placeholder="+49 170 1234567" className="pl-8 h-10" data-testid="caller-number-input" />
                        </div>
                        <Button onClick={lookup} disabled={busy === "lookup"} className="h-10 bg-[#111827] text-white" data-testid="caller-lookup-btn">
                            {busy === "lookup" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-1.5" /> Suchen</>}
                        </Button>
                    </div>
                    <div className="flex items-end gap-2 border-t border-gray-100 pt-3">
                        <div>
                            <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1">Abteilung (Screen-Pop)</label>
                            <select value={dept} onChange={(e) => setDept(e.target.value)} className="h-9 text-sm border border-gray-300 rounded-sm px-2 bg-white" data-testid="caller-dept-select">
                                {DEPARTMENTS.map((d) => <option key={d.code} value={d.code}>{d.label}</option>)}
                            </select>
                        </div>
                        <Button onClick={testCall} disabled={busy === "test"} variant="outline" className="h-9" data-testid="caller-test-btn">
                            {busy === "test" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><PhoneIncoming className="w-4 h-4 mr-1.5" /> Test-Anruf simulieren</>}
                        </Button>
                    </div>
                </div>

                {dossier && (
                    <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="caller-result">
                        <CallerDossier dossier={dossier} />
                    </div>
                )}

                {logs.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-sm p-4 mt-5" data-testid="caller-history">
                        <h2 className="font-display font-bold text-sm text-[#111827] mb-2 flex items-center gap-2"><History className="w-4 h-4 text-indigo-600" /> Letzte Anrufe</h2>
                        <div className="divide-y divide-gray-100">
                            {logs.map((l) => {
                                const tel = (l.number || "").replace(/[^\d+]/g, "");
                                return (
                                    <div key={l.id} className="flex items-center justify-between gap-2 py-2" data-testid={`call-log-${l.id}`}>
                                        <button onClick={() => lookup(l.number)} className="flex-1 min-w-0 text-left hover:text-indigo-700" data-testid={`call-log-lookup-${l.id}`}>
                                            <div className="text-sm font-bold truncate">{l.matched ? (l.customer_name || "Kunde") : "Unbekannt"} <span className="font-normal text-gray-500 font-mono-num">· {l.number}</span></div>
                                            <div className="text-[10px] text-gray-400">{l.created_at ? new Date(l.created_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}{l.department ? ` · ${l.department}` : ""}</div>
                                        </button>
                                        {tel && (
                                            <a href={`tel:${tel}`} className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-2.5 py-1.5 rounded-sm shrink-0" data-testid={`call-log-callback-${l.id}`}>
                                                <PhoneCall className="w-3.5 h-3.5" /> Rückruf
                                            </a>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="mt-6 flex items-start gap-2 text-xs text-gray-500 bg-blue-50 border border-blue-100 rounded-sm p-3">
                    <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                    <span>Für den Live-Betrieb wird eine Telefonie-Nummer (z. B. Twilio) auf den Webhook <code className="bg-white px-1 rounded">/api/webhook/twilio/incoming-call</code> geleitet. Eingehende Anrufe lösen dann automatisch den Screen-Pop aus.</span>
                </div>
            </div>
        </div>
    );
}
